import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isLocale, type Locale } from '@/client/i18n';
import { openConsentSettings } from '@/client/features/consent/useConsent';

function InstagramIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="2" width="20" height="20" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}
function FacebookIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.99 3.66 9.13 8.44 9.88V14.9H7.9V12h2.54V9.8c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56V12h2.78l-.45 2.9h-2.33v6.98C18.34 21.13 22 16.99 22 12z" />
    </svg>
  );
}
function YoutubeIcon({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M21.58 7.19c-.23-.86-.9-1.53-1.76-1.76C18.25 5 12 5 12 5s-6.25 0-7.82.43c-.86.23-1.53.9-1.76 1.76C2 8.75 2 12 2 12s0 3.25.42 4.81c.23.86.9 1.53 1.76 1.76C5.75 19 12 19 12 19s6.25 0 7.82-.43c.86-.23 1.53-.9 1.76-1.76C22 15.25 22 12 22 12s0-3.25-.42-4.81zM10 15V9l5.2 3L10 15z" />
    </svg>
  );
}

const SOCIAL_ICONS = {
  instagram: InstagramIcon,
  facebook: FacebookIcon,
  youtube: YoutubeIcon,
} as const;

export function PublicFooter() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const prefix = `/${locale}`;
  const year = new Date().getFullYear();

  const legal = [
    { key: 'privacy', path: 'ochrana-sukromia' },
    { key: 'gdpr', path: 'gdpr' },
    { key: 'terms', path: 'obchodne-podmienky' },
    { key: 'cookies', path: 'cookies' },
  ] as const;

  const company = [
    { key: 'about', path: 'o-dajane' },
    { key: 'academy', path: 'o-academy' },
    { key: 'courses', path: 'kurzy' },
    { key: 'consultations', path: 'konzultacie' },
    { key: 'mentoring', path: 'mentoring' },
    { key: 'contact', path: 'kontakt' },
  ] as const;

  const socials = [
    { key: 'instagram' as const, href: 'https://instagram.com/dajanarodriguez' },
    { key: 'facebook' as const, href: 'https://facebook.com/dajanarodriguez' },
    { key: 'youtube' as const, href: 'https://youtube.com/@dajanarodriguez' },
  ];

  return (
    <footer className="relative mt-20 overflow-hidden">
      {/* Deep purple gradient base */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 20% 0%, rgba(193,167,226,0.18), transparent 55%),' +
            'radial-gradient(ellipse at 80% 100%, rgba(117,59,189,0.30), transparent 55%),' +
            'linear-gradient(180deg, #2d1548 0%, #1a0d2e 100%)',
        }}
        aria-hidden
      />
      {/* Subtle floating orb */}
      <div
        className="absolute -top-32 right-1/3 w-[28rem] h-[28rem] rounded-full pointer-events-none"
        style={{
          background:
            'radial-gradient(circle, rgba(193,167,226,0.18) 0%, transparent 65%)',
          filter: 'blur(40px)',
        }}
        aria-hidden
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 pt-20 pb-12">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <Link
              to={prefix}
              aria-label="Akadémia Dajana Rodriguez"
              className="inline-flex items-center gap-3"
            >
              <img src="/brand/symbol-white.svg" alt="" className="h-12 w-auto" onError={(e) => {
                // Fallback if -white variant isn't shipped: keep purple symbol on tint.
                (e.currentTarget as HTMLImageElement).src = '/brand/symbol-purple.svg';
              }} />
              <span className="font-display text-h3 text-white">Akadémia</span>
            </Link>
            <p className="mt-6 font-display text-body-large text-white/80 max-w-md leading-relaxed">
              {t('footer.tagline')}
            </p>

            <div className="mt-8 flex gap-3">
              {socials.map((item) => {
                const Icon = SOCIAL_ICONS[item.key];
                return (
                  <a
                    key={item.key}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t(`footer.${item.key}`)}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-pill bg-white/8 backdrop-blur-md border border-white/15 text-white/85 hover:bg-white/15 hover:border-white/30 hover:text-white transition-all hover:-translate-y-0.5"
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          <FooterColumn className="md:col-span-3" title={t('footer.section_company')}>
            {company.map((item) => (
              <Link
                key={item.key}
                to={`${prefix}/${item.path}`}
                className="font-sans text-body-small text-white/70 hover:text-white transition-colors"
              >
                {t(`nav.${item.key}`)}
              </Link>
            ))}
          </FooterColumn>

          <FooterColumn className="md:col-span-4" title={t('footer.section_legal')}>
            {legal.map((item) => (
              <Link
                key={item.key}
                to={`${prefix}/${item.path}`}
                className="font-sans text-body-small text-white/70 hover:text-white transition-colors"
              >
                {t(`footer.${item.key}`)}
              </Link>
            ))}
            <button
              type="button"
              onClick={openConsentSettings}
              className="font-sans text-body-small text-white/70 hover:text-white transition-colors text-left"
            >
              {t('footer.cookie_settings')}
            </button>
          </FooterColumn>
        </div>

        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="font-sans text-caption text-white/55">
            {t('footer.copyright', { year })}
          </p>
          <p className="font-display text-caption text-white/55">
            Premium experience for women in business.
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <h2 className="font-sans text-caption uppercase tracking-[0.18em] text-primary-light mb-5">
        {title}
      </h2>
      <ul className="space-y-3">
        {Array.isArray(children)
          ? children.map((child, idx) => <li key={idx}>{child}</li>)
          : <li>{children}</li>}
      </ul>
    </div>
  );
}
